/**
 * @graphql agg
 */
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useEffect } from 'react';

import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useSessionData } from '@confluence/session-data';
import { isUnauthorizedError, Attribution } from '@confluence/error-boundary';
import { getMonitoringClient } from '@confluence/monitoring';
import { fg } from '@confluence/feature-gating';
import type {
	useAutomatedMeetingNotesPagePropertiesQuery as useAutomatedMeetingNotesPagePropertiesQuery$data,
	useAutomatedMeetingNotesPagePropertiesQueryVariables as useAutomatedMeetingNotesPagePropertiesQuery$variables,
} from './__types__/useAutomatedMeetingNotesPagePropertiesQuery';

const createAriId = (cloudId: string, pageId: string) =>
	`ari:cloud:confluence:${cloudId}:page/${pageId}`;

type AutomatedMeetingNotesPagePropertiesResult = {
	isAMN: boolean;
	isRecurringParent: boolean;
	isLiveAMNPage: boolean;
	isLoading: boolean;
};

const useAutomatedMeetingNotesPagePropertiesQuery = gql`
	query useAutomatedMeetingNotesPagePropertiesQuery($pageAri: ID!) {
		confluence {
			page(id: $pageAri) {
				properties(keys: ["isAutomatedMeetingNotes", "meetingRecurrenceAri"]) {
					key
					value
				}
				subtype
			}
		}
	}
`;

export const useAutomatedMeetingNotesPageProperties = (
	pageId: string,
): AutomatedMeetingNotesPagePropertiesResult => {
	const { cloudId } = useSessionData();
	const { data, loading, error } = useQuery<
		useAutomatedMeetingNotesPagePropertiesQuery$data,
		useAutomatedMeetingNotesPagePropertiesQuery$variables
	>(useAutomatedMeetingNotesPagePropertiesQuery, {
		client: getAGGClient(),
		variables: {
			pageAri: createAriId(cloudId, pageId),
		},
		skip: !fg('ai_powered_meeting_notes_ga'),
	});

	useEffect(() => {
		if (error) {
			if (!isUnauthorizedError(error)) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.INTEGRATIONS,
				});
			}
			markErrorAsHandled(error);
		}
	}, [error]);

	const isAMN = data?.confluence?.page?.properties?.[0]?.value == 'true';
	const isRecurringParent = data?.confluence?.page?.properties?.[1] != null;
	const isLiveAMNPage = data?.confluence?.page?.subtype === 'LIVE' && isAMN;

	return {
		isLoading: loading,
		isAMN,
		isRecurringParent,
		isLiveAMNPage,
	};
};
